import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { EffectiveRole, normalizeRole } from "@/lib/team-roles";

/**
 * Resolves the current user's effective role on a team.
 * Team owners are always treated as "owner" (implicit admin+).
 */
export const useTeamRole = (teamId?: string | null, ownerId?: string | null) => {
  const [role, setRole] = useState<EffectiveRole | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    const resolve = async () => {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      const uid = session?.user?.id ?? null;
      if (!active) return;
      setUserId(uid);

      if (!uid || !teamId) {
        setRole(null);
        setLoading(false);
        return;
      }

      if (ownerId && ownerId === uid) {
        setRole("owner");
        setLoading(false);
        return;
      }

      const { data: team } = await supabase
        .from("teams")
        .select("owner_id")
        .eq("id", teamId)
        .maybeSingle();

      if (!active) return;

      if (team?.owner_id === uid) {
        setRole("owner");
        setLoading(false);
        return;
      }

      const { data: membership } = await supabase
        .from("team_members")
        .select("role")
        .eq("team_id", teamId)
        .eq("user_id", uid)
        .maybeSingle();

      if (!active) return;
      setRole(membership ? normalizeRole(membership.role) : null);
      setLoading(false);
    };

    resolve();
    return () => {
      active = false;
    };
  }, [teamId, ownerId]);

  return { role, userId, loading };
};

export default useTeamRole;
